/**
 * Media and Image utility functions
 * Common utilities for resolving image paths, parsing file paths, and checking file types
 */

// Get base image URL from environment variable
const getBaseImageUrlInternal = (): string => {
  return `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
};

const HTTPISH_OR_PROTOCOL_RELATIVE = /^(https?:)?\/\//i;

function isSameApiOriginUrl(entry: string): boolean {
  try {
    const u = new URL(entry.trim());
    const b = new URL(getBaseImageUrlInternal());
    return u.origin === b.origin;
  } catch {
    return false;
  }
}

function resolveVehicleImageCandidate(entry: string, baseUrl: string): string | null {
  const t = entry.trim();
  if (!t) return null;
  if (t.toLowerCase().startsWith("data:")) return null;
  if (HTTPISH_OR_PROTOCOL_RELATIVE.test(t)) {
    if (isSameApiOriginUrl(t)) return t;
    return null;
  }
  const cleaned = t.replace(/^\/+/, "");
  if (!cleaned) return null;
  return `${baseUrl}${cleaned}`;
}

export function readVehicleImagesJsonFromRow(row: unknown): string | undefined {
  if (row == null || typeof row !== "object") return undefined;
  const r = row as Record<string, unknown>;
  const raw = r.vehicleimages ?? r.VehicleImages ?? r.vehicleImages;
  return typeof raw === "string" ? raw : undefined;
}

export function pickVehicleCardImageFromApiField(vehicleimages: string | null | undefined): string {
  if (!vehicleimages || typeof vehicleimages !== "string") return "";
  const s = vehicleimages.trim();
  if (!s) return "";
  const baseUrl = getBaseImageUrlInternal();
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s) as unknown;
      if (!Array.isArray(parsed)) return "";
      for (const raw of parsed) {
        if (typeof raw !== "string") continue;
        const resolved = resolveVehicleImageCandidate(raw, baseUrl);
        if (resolved) return resolved;
      }
    } catch {
      return "";
    }
    return "";
  }
  const one = resolveVehicleImageCandidate(s, baseUrl);
  return one ?? "";
}

export const resolvePreviewSrc = (
  value: string | null | undefined,
  defaultImagePath: string = 'DefaultImage.png'
): string => {
  try {
    const v = String(value || "").trim();
    const BASE_IMAGE_URL = getBaseImageUrlInternal();
    
    // Helper to check if path is already absolute (static assets, full URLs)
    const isAbsolutePath = (path: string): boolean => {
      return path.startsWith('http') || path.startsWith('/static/') || path.startsWith('data:');
    };
    
    // Helper to resolve default image path
    const resolveDefaultPath = (): string => {
      if (isAbsolutePath(defaultImagePath)) {
        return defaultImagePath;
      }
      return `${BASE_IMAGE_URL}${defaultImagePath.replace(/^\/+/, '')}`;
    };
    
    if (!v) {
      return resolveDefaultPath();
    }
    
    if (v.startsWith("[")) {
      const picked = pickVehicleCardImageFromApiField(v);
      if (picked) return picked;
      return resolveDefaultPath();
    }
    
    // Handle absolute paths (full URLs, static assets)
    if (isAbsolutePath(v)) return v;
    
    // Handle relative paths - prepend BASE_IMAGE_URL
    const cleaned = v.replace(/^\/+/, '');
    return cleaned 
      ? `${BASE_IMAGE_URL}${cleaned}` 
      : resolveDefaultPath();
  } catch {
    const BASE_IMAGE_URL = getBaseImageUrlInternal();
    const isAbsolutePath = (path: string): boolean => {
      return path.startsWith('http') || path.startsWith('/static/') || path.startsWith('data:');
    };
    return isAbsolutePath(defaultImagePath) 
      ? defaultImagePath 
      : `${BASE_IMAGE_URL}${defaultImagePath.replace(/^\/+/, '')}`;
  }
};

/**
 * Parses comma-separated file paths into an array
 * 
 * @param value - Comma-separated string of file paths
 * @returns Array of file paths
 */
export const parseFilePaths = (value: string | null | undefined): string[] => {
  if (!value) return [];
  return value.split(',').map(path => path.trim()).filter(path => path);
};

/**
 * Checks if a file is a PDF based on its extension
 * 
 * @param filename - The filename or file path to check
 * @returns True if the file is a PDF
 */
export const isPdfFile = (filename: string | null | undefined): boolean => {
  if (!filename) return false;
  return filename.toLowerCase().endsWith('.pdf');
};

/**
 * Checks if a file is a video based on its extension
 * 
 * @param filename - The filename or file path to check
 * @returns True if the file is a video
 */
export const isVideoFile = (filename: string | null | undefined): boolean => {
  if (!filename) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
  const lowerFilename = filename.toLowerCase();
  return videoExtensions.some(ext => lowerFilename.endsWith(ext));
};

/**
 * Normalizes image path by extracting the actual path from various formats
 * Handles JSON arrays and removes common service prefixes
 * 
 * @param value - The image path/value to normalize
 * @returns Normalized image path
 */
export const normalizeImagePath = (value: string | null | undefined): string => {
  try {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";
    
    // Handle JSON array strings
    if (trimmed.startsWith("[")) {
      try {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr)) {
          for (const raw of arr) {
            const s = String(raw || "").trim();
            if (!s || s.toLowerCase().startsWith("data:")) continue;
            if (HTTPISH_OR_PROTOCOL_RELATIVE.test(s)) {
              if (isSameApiOriginUrl(s)) return s;
              continue;
            }
            return s.startsWith("/") ? s : `/${s.replace(/^\/+/, "")}`;
          }
        }
      } catch {
        // If JSON parsing fails, continue with normal processing
      }
    }
    
    // Remove common service prefix if present
    const lower = trimmed.toLowerCase();
    const marker = "/commonservice/images/";
    const idx = lower.indexOf(marker);
    if (idx !== -1) {
      const after = trimmed.substring(idx + marker.length);
      return after.startsWith("/") ? after : `/${after}`;
    }
    
    // Ensure path starts with /
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  } catch {
    return String(value || "");
  }
};

/**
 * Gets the base image URL from environment variables
 * 
 * @returns Base image URL with trailing slash
 */
export const getBaseImageUrl = (): string => {
  return getBaseImageUrlInternal();
};
